/*
  La Matcha — FAQ page (sections/faq-page.liquid).

    <faq-page>

  Search    [data-faq-search] filters the questions while you type. A question matches when every
            word of the query occurs in its question or answer. Matching ignores case the Turkish
            way (I/ı, İ/i) and ignores diacritics, so "iade", "İADE" and "siparis" all work.
            While a query is active every match is open, empty categories and their navigation
            entries are hidden and the counts show the number of matches.
  Hash      #<category anchor> opens the first question of that category, #<question id> opens
            that question; on load, on hashchange and when a navigation link is used.
  Accordion one question open at a time through <details name>; the name is taken away while a
            search is active so that every match can be open.

  Children:
    [data-faq-group]          category (its id is the anchor)
    [data-faq-item]           <details> of one question, with [data-faq-question] and [data-faq-answer]
    [data-faq-nav-item="id"]  navigation entry of a category, with [data-faq-count]
    [data-faq-empty]          empty state; [data-faq-empty-heading] has data-template with [terms]
    [data-faq-strings]        announcement texts
  Attributes:
    data-group-name           the shared <details name>
*/
(() => {
  if (customElements.get('faq-page')) return;

  const LM = window.LM || {};

  /* Lower case by Turkish rules, then without diacritics: "İade Şartları" → "iade sartlari". */
  const fold = (text) =>
    String(text || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ı/g, 'i')
      .replace(/\s+/g, ' ')
      .trim();

  class FaqPage extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.groupName = this.dataset.groupName || '';
      this.input = this.querySelector('[data-faq-search]');
      this.empty = this.querySelector('[data-faq-empty]');
      this.emptyHeading = this.querySelector('[data-faq-empty-heading]');
      this.strings = (this.querySelector('[data-faq-strings]') || {}).dataset || {};
      this.items = [...this.querySelectorAll('[data-faq-item]')].map((el) => {
        const question = el.querySelector('[data-faq-question]');
        const answer = el.querySelector('[data-faq-answer]');
        return { el, text: fold(`${question ? question.textContent : ''} ${answer ? answer.textContent : ''}`) };
      });
      this.groups = [...this.querySelectorAll('[data-faq-group]')].map((el) => {
        const nav = el.id ? this.querySelector(`[data-faq-nav-item="${CSS.escape(el.id)}"]`) : null;
        return {
          el,
          nav,
          count: nav ? nav.querySelector('[data-faq-count]') : null,
          items: this.items.filter((item) => el.contains(item.el)),
        };
      });
      this.searching = false;

      if (this.input) {
        this.input.addEventListener('input', () => this.filter());
        // A value the browser restored after a reload or a trip through the history.
        if (this.input.value.trim()) this.filter();
      }

      this.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        const link = event.target.closest('[data-faq-nav-link]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const target = this.targetOf(link.hash);
        if (!target) return;
        event.preventDefault();
        if (window.location.hash !== link.hash) window.history.pushState(null, '', link.hash);
        this.reveal(target);
      });

      this.onHashChange = () => this.revealHash();
      window.addEventListener('hashchange', this.onHashChange);
      window.addEventListener('popstate', this.onHashChange);

      this.syncSticky = this.syncSticky.bind(this);
      this.syncSticky();
      window.addEventListener('resize', this.syncSticky);

      this.revealHash();
    }

    disconnectedCallback() {
      window.removeEventListener('hashchange', this.onHashChange);
      window.removeEventListener('popstate', this.onHashChange);
      window.removeEventListener('resize', this.syncSticky);
      window.clearTimeout(this.announceTimer);
    }

    /* ---------- Search ---------- */
    filter() {
      const query = this.input.value.trim();
      const words = fold(query).split(' ').filter(Boolean);
      const searching = words.length > 0;

      if (searching && !this.searching) {
        this.items.forEach(({ el }) => {
          el.wasOpen = el.open;
          el.removeAttribute('name');
        });
      }

      let total = 0;
      this.items.forEach((item) => {
        const match = !searching || words.every((word) => item.text.includes(word));
        item.el.hidden = !match;
        if (searching) item.el.open = match;
        if (match) total += 1;
      });

      if (!searching && this.searching) {
        // Back to one open question: close the rest before the shared name returns.
        let kept = false;
        this.items.forEach(({ el }) => {
          el.open = !kept && !!el.wasOpen;
          if (el.open) kept = true;
        });
        this.items.forEach(({ el }) => {
          if (this.groupName) el.setAttribute('name', this.groupName);
        });
      }

      this.groups.forEach((group) => {
        const visible = group.items.filter((item) => !item.el.hidden).length;
        group.el.hidden = visible === 0;
        if (group.nav) group.nav.hidden = visible === 0;
        if (group.count) group.count.textContent = String(visible);
      });

      if (this.empty) {
        this.empty.hidden = total > 0;
        if (this.emptyHeading && total === 0) {
          this.emptyHeading.textContent = (this.emptyHeading.dataset.template || '[terms]').replace('[terms]', query);
        }
      }

      this.searching = searching;
      if (searching) this.announce(total);
    }

    announce(total) {
      window.clearTimeout(this.announceTimer);
      this.announceTimer = window.setTimeout(() => {
        if (!LM.announce) return;
        let message = this.strings.resultsNone;
        if (total === 1) message = this.strings.resultsOne;
        else if (total > 1) message = (this.strings.resultsOther || '').replace('[count]', String(total));
        if (message) LM.announce(message);
      }, 600);
    }

    clearSearch() {
      if (!this.input || !this.searching) return;
      this.input.value = '';
      this.filter();
    }

    /* ---------- Hash ---------- */
    targetOf(hash) {
      if (!hash || hash.length < 2) return null;
      let id = hash.slice(1);
      try {
        id = decodeURIComponent(id);
      } catch (error) {
        return null;
      }
      const el = document.getElementById(id);
      return el && this.contains(el) && el.matches('[data-faq-group], [data-faq-item]') ? el : null;
    }

    revealHash() {
      const target = this.targetOf(window.location.hash);
      if (target) this.reveal(target);
    }

    /* Opens the question (or the first question of the category), then scrolls once the layout has settled. */
    reveal(target) {
      if (target.hidden || (target.matches('[data-faq-item]') && target.closest('[data-faq-group]').hidden)) {
        this.clearSearch();
      }
      const item = target.matches('[data-faq-item]') ? target : target.querySelector('[data-faq-item]:not([hidden])');
      if (item && !this.searching) {
        this.items.forEach(({ el }) => {
          if (el !== item && el.open) el.open = false;
        });
        item.open = true;
      }
      window.requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: LM.reduceMotion && LM.reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      });
    }

    /* ---------- Layout ---------- */
    syncSticky() {
      const header = document.querySelector('[data-site-header]');
      const wrapper = header ? header.closest('.shopify-section') || header : null;
      const sticky = !!wrapper && window.getComputedStyle(wrapper).position === 'sticky';
      if (sticky) this.style.removeProperty('--sticky-top');
      else this.style.setProperty('--sticky-top', '0px');
    }
  }

  customElements.define('faq-page', FaqPage);
})();
